import type { Messages } from './sv.ts';

export const en: Messages = {
  app: {
    skip: 'Skip to content',
    offline: 'You are offline. Changes are not saved until the connection is back.',
  },
  nav: {
    main: 'Main menu',
    calendar: 'Calendar',
    rooms: 'Rooms',
    bookings: 'My bookings',
    admin: 'Administration',
    profile: 'Profile',
    signOut: 'Sign out',
    language: 'Svenska',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    edit: 'Edit',
    remove: 'Remove',
    back: 'Back',
    loading: 'Loading…',
    yes: 'Yes',
    no: 'No',
    saved: 'Saved.',
    people: 'people',
    floor: 'Floor',
    name: 'Name',
    email: 'Email',
    search: 'Search',
    all: 'All',
    active: 'Active',
    inactive: 'Deactivated',
    from: 'From',
    to: 'To',
    date: 'Date',
    start: 'Start',
    end: 'End',
    copy: 'Copy',
    copied: 'Copied!',
    download: 'Download',
    confirm: 'Confirm',
    retry: 'Try again',
    none: 'Nothing to show.',
  },
  login: {
    title: 'Sign in',
    email: 'Email address',
    password: 'Password',
    submit: 'Sign in',
    forgot: 'Forgot your password?',
    failed: 'Wrong email address or password.',
    code: 'Code from your authenticator app',
    codeHint:
      'Open the app (e.g. Google Authenticator or Microsoft Authenticator) and enter the six-digit code.',
    verify: 'Verify',
    badCode: 'That code is not right. Try again.',
    useBackup: 'Use a backup code',
    backupCode: 'Backup code',
  },
  forgot: {
    title: 'Forgot password',
    intro: 'Enter your email address and we will send a link to choose a new password.',
    submit: 'Send link',
    sent: 'If the address is registered, we have sent a link. It is valid for one hour.',
  },
  reset: {
    title: 'Choose a new password',
    submit: 'Save password',
    done: 'Your password has been changed. Sign in with the new password.',
    invalid: 'The link is invalid or has expired. Ask for a new one.',
  },
  invite: {
    title: 'Welcome, {{name}}!',
    intro: 'Choose a password for {{email}}. You sign in with your email address.',
    submit: 'Create account',
    done: 'Done! You can sign in now.',
    invalid: 'The link is invalid, already used or expired. Ask reception for a new one.',
  },
  confirmEmail: {
    title: 'Confirm email address',
    done: 'Your email address is now {{email}}.',
    invalid: 'The link is invalid or has expired.',
  },
  password: {
    new: 'New password',
    repeat: 'Repeat the password',
    rules:
      'At least 10 characters. Common passwords are not accepted. Tip: several words in a row, e.g. "cinnamon-train-sunshine".',
    mismatch: 'The passwords do not match.',
  },
  twofa: {
    title: 'Two-step sign-in',
    intro: 'As staff you need two-step sign-in. You need an authenticator app on your phone.',
    step1: 'Confirm your password to begin.',
    step2: 'Scan the QR code with the app, or type the key by hand.',
    key: 'Key',
    step3: 'Enter the code the app shows.',
    backup: 'Backup codes',
    backupIntro: 'Keep these codes somewhere safe. Each code works once if you lose your phone.',
    done: 'Two-step sign-in is on.',
    continue: 'Continue',
  },
  cal: {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    today: 'Today',
    prev: 'Previous',
    next: 'Next',
    newBooking: 'New booking',
    find: 'Find a free room',
    hint: 'Tap the calendar to book.',
    busy: 'Busy',
    blocked: 'Blocked',
    mine: 'My booking',
    room: 'Room',
    dst: 'Clock change on this day (23 or 25 hours).',
    tz: 'All times are Swedish time',
    noRooms: 'You have no rooms to book. Contact reception.',
    freeNow: 'Free now',
    busyNow: 'Busy now',
    bookings: '{{count}} bookings',
    bookings_one: '{{count}} booking',
    continues: 'continues',
  },
  booking: {
    new: 'New booking',
    edit: 'Change booking',
    title: 'Title',
    titlePlaceholder: 'Meeting',
    attendees: 'Number of people',
    room: 'Room',
    startDate: 'Start date',
    startTime: 'Start time',
    endDate: 'End date',
    endTime: 'End time',
    repeat: 'Repeat',
    repeatNone: 'No',
    repeatCount: 'Weekly, number of times',
    repeatUntil: 'Weekly until',
    times: 'Number of times',
    until: 'Until (inclusive)',
    book: 'Book',
    booked: 'Booked!',
    bookedSeries: '{{count}} bookings created.',
    forUser: 'Book for',
    forMe: 'Myself',
    override: 'Book anyway (more people than the room holds)',
    cancel: 'Cancel booking',
    cancelOne: 'Only this one',
    cancelFollowing: 'This and all following',
    cancelConfirm: 'Cancel this booking?',
    cancelled: 'Cancelled.',
    endEarly: 'End now',
    endedEarly: 'The booking has ended and the room is free.',
    scopeQuestion: 'Apply the change to',
    saveChanges: 'Save changes',
    series: 'Recurring',
    reason: 'Reason (shown to the person)',
    by: 'Booked by',
    createdBy: 'Created by',
    company: 'Company',
    status: 'Status',
    statusActive: 'Active',
    statusCancelled: 'Cancelled',
    clashes: 'These dates clash, so nothing was booked:',
    clashBooking: 'taken',
    clashBlock: 'blocked',
    capacity: 'The room holds {{count}} people.',
    multiDay: 'Several days',
    started: 'In progress',
  },
  find: {
    title: 'Find a free room',
    search: 'Search',
    none: 'No rooms are free at that time.',
    book: 'Book',
  },
  rooms: {
    equipment: 'Equipment',
    layout: 'Layout',
    description: 'Description',
    book: 'Book',
    capacity: 'Capacity',
    noPhoto: 'No photo',
    qr: 'QR sign',
    inactive: 'Not bookable',
  },
  mine: {
    upcoming: 'Upcoming',
    past: 'Past',
    none: 'No bookings.',
    notifications: 'Notifications',
    noNotifications: 'No notifications.',
    markRead: 'Mark as read',
  },
  profile: {
    title: 'Profile',
    firstName: 'First name',
    lastName: 'Last name',
    language: 'Language',
    company: 'Company',
    reminder: 'Reminder before meetings',
    reminderOff: 'Off',
    minutes: '{{count}} minutes',
    hours: '{{count}} hours',
    calendar: 'Calendar link',
    calendarIntro:
      'Subscribe to your bookings in Outlook, Google Calendar or Apple Calendar. The link is personal; do not share it.',
    calendarNew: 'Create a new link',
    calendarNewConfirm: 'The old link will stop working. Continue?',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    passwordChanged: 'Your password has been changed.',
    export: 'My data',
    exportIntro: 'Download all your bookings as a CSV file.',
    exportButton: 'Download bookings',
    deleteInfo: 'Want to change your email address or close your account? Contact reception.',
  },
  errors: {
    unauthenticated: 'You are not signed in.',
    two_factor_required: 'Two-step sign-in is required.',
    two_factor_setup_required: 'You need to turn on two-step sign-in first.',
    forbidden: 'You are not allowed to do that.',
    not_found: 'Not found.',
    validation: 'Please check the details.',
    conflict: 'The room is taken or within 15 minutes of another booking.',
    room_blocked: 'The room is blocked at that time.',
    block_overlaps_bookings: 'There are bookings in that period. Cancel them first.',
    no_access: 'You do not have access to this room.',
    over_capacity: 'The room does not hold that many people.',
    in_past: 'That time has already passed.',
    not_editable: 'This booking can no longer be changed.',
    room_inactive: 'This room cannot be booked.',
    forbidden_role_change: 'You may not change that role.',
    actor_inactive: 'Your account is deactivated.',
    site_mismatch: 'Wrong building.',
    booker_inactive: 'The person or company is deactivated.',
    too_long: 'The booking is longer than allowed.',
    too_far_ahead: 'You cannot book that far ahead.',
    room_has_future_bookings: 'The room has upcoming bookings. Cancel them first.',
    series_conflicts: 'Some dates clash.',
    email_taken: 'That email address is already in use.',
    invalid_token: 'The link is invalid or has expired.',
    weak_password:
      'The password is too weak. Use at least 10 characters and avoid common passwords.',
    rate_limited: 'Too many attempts. Wait 15 minutes and try again.',
    internal: 'Something went wrong. Try again in a moment.',
    network: 'No connection to the server.',
  },
  admin: {
    tabs: {
      reports: 'Reports',
      bookings: 'Bookings',
      rooms: 'Rooms',
      users: 'Users',
      companies: 'Companies',
      groups: 'Access groups',
      blocks: 'Blocks',
      settings: 'Settings',
      log: 'Log',
      monitor: 'Monitoring',
    },
    users: {
      invite: 'Invite a user',
      role: 'Role',
      roles: { tenant: 'Tenant', reception: 'Reception', admin: 'Admin' },
      status: {
        active: 'Active',
        invited: 'Invited, not yet active',
        inactive: 'Deactivated',
        anonymized: 'Anonymized',
      },
      send: 'Create and send invitation',
      sent: 'Invitation sent to {{email}}.',
      manualLink:
        'The account was created but email could not be sent. Give the person this link (it works once and is valid for 7 days).',
      notSent: 'The account was created but the email could not be sent.',
      resend: 'Send a new link',
      deactivate: 'Deactivate',
      activate: 'Activate',
      changeEmail: 'Change email',
      newEmail: 'New email address',
      emailChangeSent: 'A confirmation has been sent to the new address.',
      deactivateConfirm: 'Deactivate {{name}}? Upcoming bookings are cancelled. History is kept.',
      deactivated: 'Deactivated. {{count}} upcoming bookings were cancelled.',
      twofa: '2FA',
      filter: 'Filter',
    },
    companies: {
      add: 'Add company',
      groups: 'Access',
      users: 'Users',
      deactivateConfirm:
        'Deactivate {{name}}? Everyone in the company is signed out and upcoming bookings are cancelled.',
      lostAccess: 'Bookings without access',
      lostAccessIntro:
        'The company has upcoming bookings in rooms it no longer has access to. Decide what to do with them.',
    },
    groups: {
      add: 'New group',
      rooms: 'Rooms in the group',
      intro: 'A company can book the rooms in the groups it belongs to.',
    },
    rooms: {
      add: 'Add room',
      photos: 'Photos',
      upload: 'Upload photo',
      maxPhotos: 'At most 10 photos per room.',
      activeLabel: 'Bookable',
      printSign: 'Print sign',
      signText: 'Scan to book',
      capacityWarning: 'Upcoming bookings with more people than the new capacity:',
    },
    blocks: {
      new: 'Block',
      rooms: 'Rooms',
      allRooms: 'All rooms (closed day)',
      reason: 'Reason',
      created: 'Blocked.',
      overlapping: 'These bookings fall within the period. Cancel them first:',
      cancelThese: 'Cancel these and notify the people',
      removeConfirm: 'Remove the block?',
    },
    bookings: {
      filters: 'Filters',
      status: 'Status',
      export: 'Export',
      bookFor: 'Book for a tenant',
      results: '{{count}} bookings',
    },
    settings: {
      booking: 'Booking rules',
      maxMinutes: 'Longest booking (minutes, 0 = no limit)',
      maxDays: 'How far ahead people can book (days, 0 = no limit)',
      reminder: 'Default reminder for new users (minutes)',
      retention: 'Retention (GDPR, admin only)',
      bookingMonths: 'Booking history with names (months)',
      deactivatedMonths: 'Anonymize deactivated accounts after (months)',
      auditMonths: 'Keep the log for (months)',
      branding: 'Name and look',
      appName: 'Service name',
      color: 'Main colour',
      logo: 'Logo',
      uploadLogo: 'Upload logo',
      fixed: 'Fixed: 15 minutes between bookings, times in 15-minute steps, Swedish time.',
    },
    reports: {
      utilization: 'Utilization per room',
      hoursPerCompany: 'Booked hours per company',
      busiest: 'Busiest times',
      weekdays: 'Weekdays',
      hoursOfDay: 'Time of day',
      period: 'Period',
      room: 'Room',
      company: 'Company',
      hours: 'Hours',
      bookings: 'Bookings',
      share: 'Utilization',
      exportXlsx: 'Export Excel',
      exportCsv: 'Export CSV',
      basis: 'Utilization is measured against 08–18 on weekdays.',
    },
    log: { when: 'When', who: 'Who', what: 'What' },
    monitor: {
      health: 'System status',
      database: 'Database',
      email: 'Email',
      integrity: 'Data check',
      ok: 'OK',
      problems: '{{count}} problems',
      lastJobs: 'Recent runs',
      integrityIntro:
        'Checks that no bookings overlap blocks, lack access or are in inactive rooms.',
      configured: 'Configured',
      notConfigured: 'Not configured',
    },
  },
};
